import { Link } from 'react-router-dom';
import {
  Sparkles,
  TrendingUp,
  ShieldCheck,
  ListChecks,
  ArrowRight,
  Zap,
  Brain,
  Target,
  Send,
  Search,
  FileDown,
  FileUp,
  Layers,
  Wand2,
} from 'lucide-react';

const features = [
  {
    icon: Brain,
    title: '10 维 A-F 评分',
    desc: '角色匹配、技术栈、级别、薪酬、公司前景、业务方向、团队、WLB、城市、长期空间——每维都打分、加权汇总，4.0 是分水岭，低于不投。',
  },
  {
    icon: ShieldCheck,
    title: '红旗一票否决',
    desc: '禁区公司、996 触线、城市冲突、薪资腰斩、招聘方信誉问题——任何一项触发，直接降级，不让你浪费简历。',
  },
  {
    icon: Target,
    title: '6 块结构化报告',
    desc: '职位摘要 / CV 匹配 / 级别策略 / 薪酬调研 / 个性化建议 / 面试准备。STAR 故事用你 CV 真实数字，不编。',
  },
  {
    icon: Layers,
    title: '批量评估',
    desc: '一次粘多条 JD（用 --- 分隔），并发跑 3 条同时评估，自动逐条入库。批量筛选效率拉满。',
  },
  {
    icon: Send,
    title: '触达话术',
    desc: '基于已评估报告 + CV，一键生成内推请求 / 冷邮件 / hiring manager 私信 / HR 跟进，4 场景 × 3 语气可选。',
  },
  {
    icon: Search,
    title: '公司调研',
    desc: '粘公司财报 / 新闻 / 招聘风评，5 角度（业务/财务/团队/文化/红旗）整理盲区与必问问题，不联网，只基于你给的料。',
  },
  {
    icon: TrendingUp,
    title: '本土化语境',
    desc: 'P5/P6/P7 级别对照、月薪×N 包结构、字节/阿里/拼多多/AI 独角兽分类、内推优先链路——不是搬美国 SaaS。',
  },
  {
    icon: ListChecks,
    title: '应用追踪',
    desc: '13 种状态枚举（已评估/已投/一面二面终面/谈薪中/已 offer/...），每条记录评分、决策理由、自动入库，支持 JSON 备份/恢复。',
  },
  {
    icon: FileUp,
    title: 'PDF / Word 一键导入',
    desc: '把现有简历（PDF / DOCX / TXT / MD）拖到页面，浏览器本地解析成文本（不上传文件），AI 自动结构化成可编辑表单，模板永远对版。',
  },
  {
    icon: Wand2,
    title: '针对 JD 定制简历',
    desc: '评估完进详情页点"开始定制"，AI 基于这份 JD 重排经历、改写措辞、突出相关项目，生成专属版本（不动你的主版本）。严禁编造经历/数字/学历。',
  },
  {
    icon: FileDown,
    title: 'PDF 简历导出',
    desc: '3 种模板 × 6 主色 × 4 字号 × 2 纸张，浏览器原生打印为 PDF，中文排版稳定，零额外依赖。',
  },
  {
    icon: Zap,
    title: '流式秒出',
    desc: '基于 SiliconFlow DeepSeek-V3，30 秒内出完整报告，边出边看，不必干等。',
  },
];

export default function LandingPage() {
  return (
    <div className="space-y-16 pb-12">
      <section className="pt-8 sm:pt-16">
        <div className="mx-auto max-w-3xl text-center">
          <div className="mb-4 inline-flex items-center gap-1.5 rounded-full bg-accent-50 px-3 py-1 text-xs font-medium text-accent-700 ring-1 ring-accent-200">
            <Sparkles className="h-3 w-3" />
            v0.5 · 评估 / 批量 / 触达 / 调研 / PDF 上传 + 针对 JD 定制简历
          </div>
          <h1 className="text-4xl font-bold leading-tight text-ink-900 sm:text-5xl">
            少投 100 份简历，
            <br className="sm:hidden" />
            多拿 3 个 offer
          </h1>
          <p className="mt-5 text-lg leading-relaxed text-ink-600">
            careerops 是一个面向中国求职者的智能求职助手。粘一段 JD，AI 用你的简历做 10 维评分、
            红旗排查、级别定位、薪酬调研、个性化建议和面试准备。
            <br className="hidden sm:inline" />
            不是机器人海投——是<span className="text-ink-900 font-medium">用脑子筛</span>。
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/evaluate" className="btn-primary">
              <Sparkles className="h-4 w-4" />
              立即试用（无需登录）
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/resume" className="btn-secondary">
              <FileUp className="h-4 w-4" />
              上传我的 PDF / Word 简历
            </Link>
          </div>
          <p className="mt-3 text-xs text-ink-400">
            匿名访客 5 次/天免费 · 登录后 30 次/天 + 跨设备同步 · 不卖数据
          </p>
        </div>
      </section>

      <section>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {features.map(f => {
            const Icon = f.icon;
            return (
              <div key={f.title} className="card transition hover:shadow-md">
                <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-accent-50 text-accent-600">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="font-semibold text-ink-900">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-ink-600">{f.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="card bg-gradient-to-br from-accent-50 via-white to-ink-50">
        <div className="grid gap-6 sm:grid-cols-2 sm:items-center">
          <div>
            <h2 className="text-2xl font-bold text-ink-900">不止评估，还管追踪</h2>
            <p className="mt-3 text-ink-600 leading-relaxed">
              每一条评估自动进 tracker，标好评分、决策、理由。
              简历投出去之后，状态从「已投」走到「一面 / 二面 / 终面 / 已 offer / 谈薪中」，
              整个 pipeline 一目了然，复盘的时候知道哪类公司转化好、哪类岗位浪费了精力。
            </p>
            <Link to="/tracker" className="btn-secondary mt-5">
              <ListChecks className="h-4 w-4" />
              查看我的 tracker
            </Link>
          </div>
          <div className="rounded-xl border border-ink-200 bg-white p-4 font-mono text-xs text-ink-700">
            <div className="mb-2 font-semibold text-ink-500">data/applications</div>
            <pre className="whitespace-pre-wrap leading-relaxed">{`| 编号 | 公司       | 岗位          | 评分 | 决策    | 状态     |
|------|-----------|---------------|------|---------|----------|
| 003  | 月之暗面   | 推理工程师    | A    | 投      | 一面     |
| 002  | 字节       | 算法 LLM      | B+   | 内推优先 | 已投     |
| 001  | XX 创业    | 后端          | D    | 不投    | 已归档   |
`}</pre>
          </div>
        </div>
      </section>

      <section className="text-center">
        <h2 className="text-2xl font-bold text-ink-900">三步开始</h2>
        <div className="mt-8 grid gap-6 sm:grid-cols-3">
          {[
            { step: '1', title: '粘 JD', desc: '复制 BOSS / 拉勾 / 公众号上的招聘文本，贴进评估框' },
            { step: '2', title: '看报告', desc: '30 秒流式出 6 块报告 + 评分 + 决策建议' },
            { step: '3', title: '决定怎么投', desc: 'A/B 全力定制，C 试水，D 以下劝退；先问内推' },
          ].map(s => (
            <div key={s.step} className="rounded-xl bg-white p-6 ring-1 ring-ink-200">
              <div className="mx-auto mb-3 grid h-10 w-10 place-items-center rounded-full bg-accent-500 text-white font-bold">
                {s.step}
              </div>
              <h3 className="font-semibold text-ink-900">{s.title}</h3>
              <p className="mt-2 text-sm text-ink-600">{s.desc}</p>
            </div>
          ))}
        </div>
        <Link to="/evaluate" className="btn-primary mt-8">
          <Sparkles className="h-4 w-4" />
          开始第一次评估
        </Link>
      </section>
    </div>
  );
}
